/**
 * Pure helpers for building DuckDB SQL expressions from user-supplied
 * value transforms (categorical mapping + numeric bucketing).
 *
 * Kept separate from the worker so it can be unit-tested without
 * instantiating DuckDB. Mirrors the pattern used by `column-overrides`.
 */
import type { ColumnTransform } from '@/types/query';
import { escId, escStr } from '@/engine/sql-utils';

const DEFAULT_OTHER = 'Diğer';

/**
 * Turkish-aware case-folding SQL expression. DuckDB's LOWER() doesn't
 * handle İ→i or keep ı as-is correctly for Turkish, so we pre-normalise
 * with REPLACE before lowering:
 *   İ (U+0130) → i
 *   I           → ı → (then LOWER keeps it)
 * After REPLACE + LOWER the comparison is fully locale-insensitive for
 * the Turkish alphabet.
 */
function trLower(expr: string): string {
  // 1. İ (dotted capital I, U+0130) → 'i'
  // 2. I (ASCII capital I) → 'ı' (dotless lowercase, so LOWER doesn't
  //    turn it into 'i' which would collide with İ→i above in non-TR locales)
  // 3. Finally LOWER() folds Ş→ş, Ç→ç, Ğ→ğ, Ö→ö, Ü→ü etc.
  return `LOWER(REPLACE(REPLACE(CAST(${expr} AS VARCHAR), 'İ', 'i'), 'I', 'ı'))`;
}

/**
 * Normalise a JS string the same way `trLower` normalises inside SQL.
 * Used for the literal side of WHEN comparisons.
 */
function trLowerJs(s: string): string {
  return s
    .replace(/İ/g, 'i')
    .replace(/I/g, 'ı')
    .toLowerCase();
}

/**
 * Build a CASE expression that rewrites the column value according to
 * the transform. The expression evaluates to a VARCHAR; keep that in
 * mind when chaining with numeric operators (buckets are labeled, not
 * numeric ranges — compare the labels as strings).
 *
 * Map-kind transforms use Turkish-aware case-insensitive matching so
 * 'istanbul', 'Istanbul', 'İSTANBUL' all resolve to the same label.
 *
 * The expression does NOT include the `AS alias` suffix — callers
 * decide whether to use it in SELECT (with alias) or WHERE (bare).
 */
export function buildTransformExpr(t: ColumnTransform): string {
  const esc = escId(t.column);
  if (t.kind === 'map') {
    const entries = Object.entries(t.mapping);
    if (entries.length === 0) {
      // No mapping rules — collapse to ELSE only.
      return t.fallback === 'other'
        ? `${escStr(t.otherLabel ?? DEFAULT_OTHER)}`
        : `CAST(${esc} AS VARCHAR)`;
    }
    const lowerCol = trLower(esc);
    const whens = entries
      .map(
        ([src, dst]) =>
          `WHEN ${lowerCol} = ${escStr(trLowerJs(src))} THEN ${escStr(dst)}`,
      )
      .join(' ');
    const elseExpr =
      t.fallback === 'other'
        ? escStr(t.otherLabel ?? DEFAULT_OTHER)
        : `CAST(${esc} AS VARCHAR)`;
    return `CASE ${whens} ELSE ${elseExpr} END`;
  }

  // bin
  if (t.bins.length === 0) {
    return `CAST(${esc} AS VARCHAR)`;
  }
  const whens = t.bins
    .map((b) => {
      const conds: string[] = [];
      if (b.min !== undefined) conds.push(`${esc} >= ${b.min}`);
      if (b.max !== undefined) conds.push(`${esc} < ${b.max}`);
      const cond = conds.length > 0 ? conds.join(' AND ') : 'TRUE';
      return `WHEN ${cond} THEN ${escStr(b.label)}`;
    })
    .join(' ');
  const elseExpr = escStr(t.otherLabel ?? DEFAULT_OTHER);
  return `CASE ${whens} ELSE ${elseExpr} END`;
}
